import React, { useState, useEffect, useRef } from "react";
import clsx from "clsx";
import styles from "./chatwindow.module.css";
import ResponseFeedback from "../ResponseFeedback";
import ThoughtsDrawer from "../ThoughtsDrawer";
import AgentTimeline, { TimelineStep } from "../AgentTimeline";
import MarkdownRenderer from "../MarkdownRenderer";
import ResponseSkeleton from "../ResponseSkeleton";
import FollowUpQuestions from "../FollowUpQuestions";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import FilePresentOutlinedIcon from "@mui/icons-material/FilePresentOutlined";
import BookOutlinedIcon from "@mui/icons-material/BookOutlined";
import SourceOutlinedIcon from "@mui/icons-material/SourceOutlined";
import QuestionAnswerOutlinedIcon from "@mui/icons-material/QuestionAnswerOutlined";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import type { StreamingStep } from "../../store/chatSlice";

export type Message = {
  id: string;
  sender: "user" | "agent";
  text: JSX.Element | string;
  timestamp?: number;
  summary?: string;
  related_info?: string;
  suggested_followups?: string;
  sources?: any[];
  backendMessageId?: string;
  isPersisted?: boolean;
  feedback?: boolean | null;
};

type MessageBubbleProps = {
  messages: Message[];
  userAvatar?: string;
  agentAvatar?: string;
  sessionId?: string;
  loading?: boolean;
  onFollowupClick?: (question: string) => void;
  drawerOpen?: boolean;
  setDrawerOpen?: (open: boolean) => void;
};

type MessageWithSteps = Message & {
  streamingSteps?: StreamingStep[];
};

const MessageBubble: React.FC<MessageBubbleProps> = ({
  messages,
  userAvatar,
  agentAvatar,
  sessionId,
  loading = false,
  onFollowupClick,
  drawerOpen: externalDrawerOpen,
  setDrawerOpen: externalSetDrawerOpen,
}) => {
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [internalDrawerOpen, setInternalDrawerOpen] = useState(false);
  const [activeMessageId, setActiveMessageId] = useState<string | null>(null);
  const messageContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastMessageEndRef = useRef<HTMLDivElement>(null);

  const globalStreamingSteps = useSelector(
    (state: RootState) => state.chat.streamingSteps
  );
  const isStreaming = useSelector((state: RootState) => state.chat.isStreaming);

  // Use external drawer state if provided, otherwise use internal state
  const drawerOpen =
    externalDrawerOpen !== undefined ? externalDrawerOpen : internalDrawerOpen;
  const setDrawerOpen = externalSetDrawerOpen || setInternalDrawerOpen;

  const drawerContentRef = useRef<HTMLDivElement>(null);

  // Auto-open drawer when streaming starts and clear active message to show live stream
  // useEffect(() => {
  //   if (isStreaming) {
  //     // When streaming starts, clear active message ID so drawer shows live global steps
  //     setActiveMessageId(null);
  //     setDrawerOpen(true);
  //   } else if (globalStreamingSteps.length > 0 && !activeMessageId) {
  //     // When streaming completes, set active message to the last agent message
  //     const lastAgentMessage = [...messages]
  //       .reverse()
  //       .find((m) => m.sender === "agent");
  //     if (lastAgentMessage) {
  //       setActiveMessageId(lastAgentMessage.id);
  //     }
  //     setDrawerOpen(true);
  //   }
  // }, [isStreaming, globalStreamingSteps.length, setDrawerOpen, messages]);

  // Auto-scroll drawer content when new streaming steps arrive
  useEffect(() => {
    const drawerContent = drawerContentRef.current;
    if (!drawerContent) return;

    // Create observer to watch for content changes
    const observer = new MutationObserver(() => {
      // console.log('Drawer content changed, scrolling...');
      drawerContent.scrollTop = drawerContent.scrollHeight;
    });

    observer.observe(drawerContent, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => observer.disconnect();
  }, [drawerOpen]); // Attach observer when drawer opens
  // Convert streaming steps to timeline format
  const convertToTimelineSteps = (steps: StreamingStep[]): TimelineStep[] => {
    return steps.map((step) => {
      let title = "";
      let subtitle = step.message;
      let sources: { url: string }[] | undefined;

      // Map step names to readable titles
      switch (step.step) {
        case "query_received":
          title = "Query Received";
          subtitle = `Processing: ${step.query || ""}`;
          break;
        case "retrieval_started":
          title = "Searching Knowledge Base";
          subtitle = step.message || "Retrieving relevant documents...";
          break;
        case "retrieval_completed":
          title = "Retrieval Completed";
          subtitle = `Found ${step.num_records || 0} relevant documents`;
          if (step.documents && step.documents.length > 0) {
            sources = step.documents.map((doc: any) => ({
              url: doc.url || doc.name || "",
            }));
          }
          break;
        case "building_sources":
          title = "Building Sources";
          subtitle = step.message || "Processing retrieved documents...";
          break;
        case "answer_generated":
          title = "Answer Generated";
          subtitle = `Generated ${step.answer_length || 0} characters, cited ${step.cited_documents?.length || 0} documents`;
          if (step.cited_documents && step.cited_documents.length > 0) {
            sources = step.cited_documents.map((doc: any) => ({
              url: doc.url || doc.name || "",
            }));
          }
          break;
        case "summary_generated":
          title = "Summary Generated";
          subtitle = step.message;
          break;
        case "completed":
          title = "Research Completed";
          subtitle = "Answer ready";
          break;
        default:
          title = step.step
            .replace(/_/g, " ")
            .replace(/\b\w/g, (l) => l.toUpperCase());
          break;
      }

      return {
        title,
        subtitle,
        time: step.timestamp
          ? new Date(step.timestamp).toLocaleTimeString()
          : undefined,
        ...(sources && sources.length > 0 && { sources }),
      };
    });
  };

  // Auto-scroll to bottom when new messages arrive or loading state changes
  useEffect(() => {
    if (loading || isInitialLoad) {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }
      setIsInitialLoad(false);
    } else {
      if (lastMessageEndRef.current) {
        lastMessageEndRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }
  }, [messages, loading]);

  return (
    <div className={styles.chatWindow}>
      <div className={styles.messageContainer} ref={messageContainerRef}>
        {/* Chat messages will be rendered here */}
        {messages?.map((msg, index) => (
          <div
            ref={index === messages.length - 2 ? lastMessageEndRef : null}
            key={msg.id}
            className={clsx(
              styles.messageBubble,
              msg.sender === "user" && styles.userBubble
            )}
          >
            {msg.sender === "agent" && agentAvatar && (
              <div className={styles.avatar}>
                <img
                  src={agentAvatar}
                  alt="Agent avatar"
                  height={24}
                  aria-label="Agent avatar"
                  role="img"
                />
              </div>
            )}
            <div
              className={clsx(
                styles.message,
                msg.sender === "user" && styles.userMessage
              )}
            >
              {msg.sender === "agent" ? (
                <>
                  {/* Summary Section */}
                  {msg.summary && (
                    <div className={styles.summarySection}>
                      <h3>
                        <AssignmentOutlinedIcon />
                        <span>Quick Summary</span>
                      </h3>
                      <MarkdownRenderer
                        content={msg.summary}
                        className={styles.markdown}
                      />
                    </div>
                  )}

                  {/* Main Answer */}
                  <div className={styles.answerSection}>
                    <h3>
                      <QuestionAnswerOutlinedIcon />
                      <span>Answer</span>
                    </h3>
                    <MarkdownRenderer content={msg.text.toString()} />
                  </div>

                  {/* Related Information */}
                  {msg.related_info && (
                    <div className={styles.relatedInfoSection}>
                      <h3>
                        <FilePresentOutlinedIcon />
                        <span>Related Information</span>
                      </h3>
                      <MarkdownRenderer
                        content={msg.related_info}
                        className={styles.markdown}
                      />
                    </div>
                  )}

                  {/* Sources */}
                  {msg.sources && msg.sources.length > 0 && (
                    <div className={styles.sourcesSection}>
                      <h3>
                        <SourceOutlinedIcon />
                        <span>Sources</span>
                      </h3>
                      <div className={styles.sourcesList}>
                        {msg.sources.map((source: any, index: number) => {
                          const docName =
                            source.metadata?.document_display_name ||
                            `Source ${index + 1}`;
                          return (
                            <div
                              key={source.uid || index}
                              className={styles.sourceItem}
                              onClick={() =>
                                source.metadata?.document_url
                                  ? window.open(
                                      source.metadata?.document_url,
                                      "_blank"
                                    )
                                  : null
                              }
                            >
                              <div className={styles.sourceDocument}>
                                {docName}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Suggested Follow-ups */}
                  {msg.suggested_followups && index === messages.length - 1 && (
                    <div className={styles.followupsSection}>
                      <h3>
                        <BookOutlinedIcon />
                        <span>Suggested Follow-ups</span>
                      </h3>
                      <FollowUpQuestions
                        content={msg.suggested_followups}
                        onQuestionClick={onFollowupClick}
                        disabled={loading}
                      />
                    </div>
                  )}
                </>
              ) : (
                msg.text
              )}
              {msg.sender === "agent" && (
                <ResponseFeedback
                  messageId={msg.backendMessageId || msg.id}
                  sessionId={sessionId || ""}
                  isPersisted={msg.isPersisted}
                  feedback={msg.feedback}
                  handleCopy={() => {
                    navigator.clipboard.writeText(msg.text.toString());
                  }}
                  handleThoughtsDrawerOpen={() => {
                    setActiveMessageId(msg.id);
                    setDrawerOpen(true);
                  }}
                />
              )}
            </div>
          </div>
        ))}

        {/* Show skeleton loader while waiting for agent response */}
        {loading && (
          <div className={clsx(styles.messageBubble)}>
            {agentAvatar && (
              <div className={styles.avatar}>
                <img
                  src={agentAvatar}
                  alt="Agent avatar"
                  height={24}
                  aria-label="Agent avatar"
                  role="img"
                />
              </div>
            )}
            <div className={styles.message}>
              <ResponseSkeleton />
            </div>
          </div>
        )}

        {/* Invisible anchor element for auto-scroll */}
        <div ref={messagesEndRef} />
      </div>
      <ThoughtsDrawer
        ref={drawerContentRef}
        title="Chain of thoughts"
        drawerOpen={drawerOpen}
        onCloseDrawer={() => setDrawerOpen(false)}
      >
        {(() => {
          // Get steps for the active message
          const activeMessage = messages.find(
            (m) => m.id === activeMessageId
          ) as MessageWithSteps | undefined;

          // Priority logic:
          // 1. If streaming OR no active message selected -> show live global steps
          // 2. If active message has steps -> show those steps
          // 3. Otherwise -> show global steps as fallback
          let stepsToDisplay: StreamingStep[] =
            activeMessage?.streamingSteps || [];
          if (isStreaming || !activeMessageId) {
            // Show live streaming steps for current query
            stepsToDisplay = globalStreamingSteps;
            // console.log('✅ Using global steps (streaming or no active message)');
          } else if (
            activeMessage?.streamingSteps &&
            activeMessage.streamingSteps.length > 0
          ) {
            // Show saved steps for specific message
            stepsToDisplay = activeMessage.streamingSteps;
            // console.log('✅ Using message steps');
          } else if (globalStreamingSteps.length > 0) {
            // Fallback to global steps
            stepsToDisplay = globalStreamingSteps;
            // console.log('✅ Using global steps (fallback)');
          }

          return stepsToDisplay.length > 0 ? (
            <AgentTimeline timeline={convertToTimelineSteps(stepsToDisplay)} />
          ) : (
            <div
              style={{ padding: "20px", textAlign: "center", color: "#666" }}
            >
              {isStreaming
                ? "Waiting for response..."
                : "No chain of thoughts data available"}
            </div>
          );
        })()}
      </ThoughtsDrawer>
    </div>
  );
};

export default React.memo(MessageBubble);
